import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import useSWR from "swr";

import { BaseContext, UserActionTypes, UserContext } from "~context";
import {
  LoginInput,
  MeQuery,
  RegisterInput,
  SocialAuthType,
  TwoFactorAuthenticationCodeInput,
  VerifyEmailInput,
  VerifyMagicLinkMutation,
} from "~graphql/sdk";
import { sdk } from "~lib";
import { getError, handlePromise, showToast } from "~lib/helpers";
import { useReCaptchaSDK } from "./useReCaptcha";

const authUrls = ["/login", "/signup"];

const getSession = async (_: string, orgId: string) => sdk({ orgId }).me();

const swrKey = "me";

export interface LoginParams {
  magicLinkInput?: VerifyEmailInput;
  loginInput?: LoginInput;
}
export const useAccount = () => {
  const router = useRouter();
  const { organization } = useContext(BaseContext);

  const { recaptchaSdkGenerator } = useReCaptchaSDK();
  const {
    user,
    dispatch,
    isLoggedIn,
    hasLoaded,
    isAdmin,
    isPOS,
    isSalesOutlet,
    hasCheckedLogin,
    setHasCheckedLogin,
    isTwoFactorAuthenticated,
    isEventManager,
    userRoles,
  } = useContext(UserContext);
  const [isLoggingIn, setLoggingIn] = useState(false);
  const [checkInitialising, setCheckInitialising] = useState<boolean>(
    undefined
  );

  const { data: userData, error, mutate, isValidating } = useSWR<
    MeQuery,
    Error
  >(
    organization?.id && (isLoggedIn || isLoggingIn || checkInitialising)
      ? [swrKey, organization?.id]
      : null,
    getSession,
    {
      shouldRetryOnError: false,
    }
  );

  const initialLoggedInCheck = async () => {
    const { data, error } = await handlePromise(async () =>
      sdk({ orgId: organization.id }).isLoggedIn()
    );

    setHasCheckedLogin(true);

    if (error) {
      showToast(getError(error, "graphQL"), "error");
      return;
    }

    setCheckInitialising(data.isLoggedIn);
  };

  useEffect(() => {
    if (isLoggedIn) {
      setCheckInitialising(!isLoggedIn);
    }
  }, [isLoggedIn]);

  // On inital load, check if the user is logged in
  // before attempting to query me(). This is to
  // prevent quering me() while not logged in as it causes
  // unauthorized errors.
  useSWR(hasCheckedLogin ? null : ["isloggedIn"], initialLoggedInCheck, {
    shouldRetryOnError: false,
    revalidateOnFocus: true,
  });

  useEffect(() => {
    if (!userData && error && !isValidating) {
      dispatch({ type: UserActionTypes.LOGOUT });
    } else if (userData && !error) {
      dispatch({ type: UserActionTypes.LOGIN, payload: userData.me });
    }
  }, [error, userData]);

  useEffect(() => {
    if (
      isLoggedIn &&
      user?.isTwoFactorAuthenticationEnabled &&
      !isTwoFactorAuthenticated &&
      router?.pathname !== "/authenticate"
    ) {
      void router?.push("/authenticate");
    } else if (isLoggedIn && authUrls.includes(router?.pathname)) {
      const url = `/${(router.query.redirect as string) || ""}`;
      void router?.push(url);
    }
  }, [isLoggedIn, router]);

  const isVerifyEmailInput = (
    input: VerifyEmailInput | LoginInput
  ): input is VerifyEmailInput => !!Object.hasOwnProperty.call(input, "token");

  const isLoginInput = (
    input: VerifyEmailInput | LoginInput
  ): input is LoginInput => !!Object.hasOwnProperty.call(input, "password");

  async function login(input: LoginInput): Promise<boolean>;
  async function login(
    input: VerifyEmailInput
  ): Promise<VerifyMagicLinkMutation>;
  async function login(
    input: LoginInput | VerifyEmailInput
  ): Promise<boolean | VerifyMagicLinkMutation> {
    setLoggingIn(true);
    if (isVerifyEmailInput(input)) {
      const { data, error } = await handlePromise(async () =>
        sdk({ orgId: organization?.id }).verifyMagicLink({ input })
      );

      if (error) {
        showToast(getError(error, "graphQL"), "error");
        return false;
      }

      void mutate(undefined, true);
      return data;
    }

    if (isLoginInput(input)) {
      const sdkLoginFn = await recaptchaSdkGenerator("login");
      const { error } = await handlePromise(async () =>
        sdkLoginFn({ orgId: organization?.id }).login({ input })
      );

      if (error) {
        showToast(getError(error, "graphQL"), "error");
        setLoggingIn(false);
        return false;
      }

      await mutate(undefined, true);
      setLoggingIn(false);
      return true;
    }

    return false;
  }

  type RouterParams = Parameters<typeof router.replace>;
  async function logout(): Promise<void>;
  async function logout(...replaceWith: RouterParams): Promise<void>;
  async function logout(...replaceWith: RouterParams | []): Promise<void> {
    const { error } = await handlePromise(async () =>
      sdk({ orgId: organization?.id }).logout()
    );

    if (error) {
      return showToast(getError(error, "graphQL"), "error");
    }

    void mutate(null, false);

    dispatch({ type: UserActionTypes.LOGOUT });

    const defaultRoute: RouterParams = ["/", undefined, undefined];
    void router.replace(...(replaceWith.length ? replaceWith : defaultRoute));

    return showToast("Logged out successfully", "success");
  }

  const logoutAfterDelete = () => {
    void mutate(null, false);
    dispatch({ type: UserActionTypes.LOGOUT });
    void router.replace("/");
  };

  const register = async (input: RegisterInput) => {
    const sdkRegisterFn = await recaptchaSdkGenerator("register");
    const { error } = await handlePromise(async () =>
      sdkRegisterFn({ orgId: organization?.id }).register({ input })
    );

    if (error) {
      showToast(getError(error, "graphQL"), "error");
      return false;
    }

    return { hasVerifiedEmail: false };
  };

  const authenticateWithTwoFactor = async (
    input: TwoFactorAuthenticationCodeInput
  ): Promise<boolean> => {
    setLoggingIn(true);

    const { error } = await handlePromise(async () =>
      sdk({ orgId: organization?.id }).authenticateWithTwoFactor({ input })
    );

    if (error) {
      showToast(getError(error, "graphQL"), "error");
      return false;
    }

    dispatch({
      type: UserActionTypes.AUTHENTICATE_TWO_FACTOR,
    });

    showToast("Authenticated successfully", "success");

    void mutate(undefined, true);
    setLoggingIn(false);

    void router.replace("/");
    return true;
  };

  const handleSocialSuccess = async (
    accessToken: string,
    type?: SocialAuthType
  ): Promise<void> => {
    setLoggingIn(true);

    const { error } = await handlePromise(async () =>
      sdk({ orgId: organization?.id }).socialAuth({
        input: {
          socialAuthType: type || SocialAuthType.Facebook,
          accessToken,
        },
      })
    );

    if (error) {
      setLoggingIn(false);
      showToast(getError(error, "graphQL"), "error");
      return;
    }

    void mutate(undefined, true);
    setLoggingIn(false);
  };

  return {
    dispatch,
    user,
    error,
    isLoggedIn,
    isLoggingIn,
    hasLoaded,
    handleSocialSuccess,
    login,
    register,
    logout,
    isAdmin,
    isPOS,
    logoutAfterDelete,
    revaildateUser: async () => mutate(undefined, true),
    authenticateWithTwoFactor,
    isTwoFactorAuthenticated,
    isValidating,
    isSalesOutlet,
    hasCheckedLogin,
    isEventManager,
    userRoles,
    isInitialising: checkInitialising,
  };
};
